import { useEffect, useMemo, useState } from "react"
import _map from "lodash/map"
import _round from "lodash/round"

import useGtm from "@src/hooks/use-gtm"
import { useAppDispatch, useAppSelector } from "@src/hooks/use-redux"

import { CART_TRANSFER, PAY_WITH_CARD } from "@src/operations/api/cart"

import { getUser, logOut } from "@src/redux/user.slice"

import apolloClient from "@src/utilities/apollo-client"
import classNames from "@src/utilities/class-names"
import { formatCurrency } from "@src/utilities/format-numbers"

import getCart from "@src/workers/api/get-cart"
import addDiscountCode from "@src/workers/api/add-discount-code"
import removeDiscountCode from "@src/workers/api/remove-discount-code"
import setCartShipping from "@src/workers/api/set-cart-shipping"

// import CheckoutApplePayButton from "@src/components/CheckoutApplePayButton"
import CheckoutBreadcrumbs, {
  type CheckoutBreadcrumbsProps,
} from "@src/components/CheckoutBreadcrumbs"
import CheckoutPrePurchase from "@src/components/CheckoutPrePurchase"
import CheckoutShippingForm, {
  type CheckoutShippingRequest,
} from "@src/components/CheckoutShippingForm"
import CheckoutCartSummary from "@src/components/CheckoutCartSummary"
import AuthorizeNetPaymentForm from "@src/components/AuthorizeNetPaymentForm"
import CheckoutPhoneForm from "@src/components/CheckoutPhoneForm"
import LoadingFullScreen from "@src/components/LoadingFullScreen"
import YotpoLoyaltyLoader from "@src/components/yotpo-loyalty"

import SplitScreenLayout from "@src/layouts/split-screen"

import type {
  GetFullCartQuery,
  PayWithCardMutation,
  PayWithCardMutationVariables,
  TransferCartToNewUidMutation,
  TransferCartToNewUidMutationVariables,
} from "@src/entities/types/api"
import CheckoutDiscountCode from "@src/components/CheckoutDiscountCode"
import {
  deltaEightRestrictedStates,
  thcaRestrictedStates,
  thcRestrictedStates,
} from "@src/utilities/state-restrictions"

export default function CheckoutShippingPage() {
  const dispatch = useAppDispatch()
  const user = useAppSelector(getUser)
  const [discountCode, setDiscountCode] = useState<string>("")
  const [discountCodeError, setDiscountCodeError] = useState<string | null>(
    null
  )
  const [emailAddress, setEmailAddress] = useState<string>("")
  const [emailAddressSuggestion, setEmailAddressSuggestion] = useState<
    string | null
  >(null)

  const [billingEnabled, setBillingEnabled] = useState<boolean>(false)
  const [isProcessing, setIsProcessing] = useState<boolean>(true)
  const [processingText, setProcessingText] = useState<string>(
    "Preparing your secure checkout"
  )
  const [hideProcessingScreen, setHideProcessingScreen] = useState<number>(0)
  const [showMobileCartSummary, setShowMobileCartSummary] =
    useState<boolean>(false)

  const [cart, setCart] = useState<
    GetFullCartQuery["cart"] | null | undefined
  >()
  const [checkoutStep, setCheckoutStep] =
    useState<CheckoutBreadcrumbsProps["step"]>("shipping")
  const [cartErrors, setCartErrors] = useState<string[]>([])
  const [paymentErrors, setPaymentErrors] = useState<string[]>([])

  const [previousUid, setPreviousUid] = useState<string | null>(null)
  const [phoneNumber, setPhoneNumber] = useState<string>("")
  const [initialShippingState, setInitialShippingState] = useState<
    string | null
  >(null)
  const [hasDeltaEight, setHasDeltaEight] = useState<boolean | null>(null)
  const [hasThc, setHasThc] = useState<boolean | null>(null)
  const [hasThca, setHasThca] = useState<boolean | null>(null)

  const getCartAndUser = async () => {
    return getCart().then(({ data, errors }) => {
      if (errors) {
        setProcessingText(_map(errors, (error) => error.message).join(", "))
        setTimeout(() => {
          window.location.href = "/shop"
        }, 2000)
        return
      }
      if (!data.cart || data.cart.lineItems.length === 0) {
        setProcessingText(
          "You do not have any items in your cart. Heading to the shop!"
        )
        // window.location.href = "/shop"
        return
      }

      setInitialShippingState(
        data.cart?.shippingAddress?.state ?? user.state ?? ""
      )

      setHasThc(
        (data.cart.lineItems ?? []).some((item) => item.product.isThc)
      )
      setHasThca(
        (data.cart.lineItems ?? []).some((item) => item.product.isThca)
      )
      setHasDeltaEight(
        (data.cart.lineItems ?? []).some((item) => item.product.isDelta8)
      )

      setCart(data.cart)
      setPhoneNumber(
        data.cart?.shippingAddress?.phoneNumber ?? user.phoneNumber ?? ""
      )
      useGtm({
        event: "orTrack",
        eventName: "Checkout Started",
        properties: {
          products: _map(data.cart?.lineItems ?? [], (lineItem) => ({
            ...(lineItem.product.categoryName && {
              category: lineItem.product.categoryName,
            }),
            ...(lineItem.frequencyInWeeks && {
              frequencyInWeeks: lineItem.frequencyInWeeks,
            }),
            ...(lineItem.product.thumbnailUrl && {
              image_url: lineItem.product.thumbnailUrl,
            }),
            name: lineItem.product.name,
            price: _round(0.01 * lineItem.priceInCart, 2),
            product_id: lineItem.product.sku,
            quantity: lineItem.quantity,
            sku: lineItem.product.sku,
            ...(lineItem.product.description && {
              variant: lineItem.product.description,
            }),
            value: _round(lineItem.quantity * 0.01 * lineItem.priceInCart, 2),
          })),
          currency: "USD",
          revenue: _round(
            0.01 * (data.cart.totals.subtotal - data.cart.totals.discounts),
            2
          ),
          discount: _round(0.01 * data.cart.totals.discounts, 2),
          shipping: _round(0.01 * data.cart.totals.shipping, 2),
          taxes: _round(0.01 * data.cart.totals.taxes, 2),
        },
      })
    })
  }

  const getSalesTaxDescriptionLine = (): string | null => {
    if (billingEnabled) {
      return null
    }
    if (!user.authStateKnown || user.isAnonymous) {
      return "Calculated in the next step"
    }
    return "Please validate your address"
  }

  const onSubmitDiscountCode = async (): Promise<string | null> => {
    const code = discountCode.trim().toUpperCase()
    if (code.length === 0) {
      return "No discount code entered"
    }
    setProcessingText("Adding your discount code")
    setIsProcessing(true)
    useGtm({
      event: "orTrack",
      eventName: "Coupon Entered",
      properties: {
        coupon_id: code,
      },
    })
    setCartErrors([])
    setPaymentErrors([])
    const { data, errors } = await addDiscountCode(code)
    if (errors) {
      const errorMessage = _map(errors, (error) => error.message).join(",")
      useGtm({
        event: "orTrack",
        eventName: "Coupon Denied",
        properties: {
          coupon_id: code,
          reason: errorMessage,
        },
      })
      setHideProcessingScreen((prevState) => prevState + 1)
      return errorMessage
    }
    setCart(data?.addDiscount)
    useGtm({
      event: "orTrack",
      eventName: "Coupon Applied",
      properties: {
        coupon_id: code,
        coupon_name: data?.addDiscount.discountName,
        discount: _round(0.01 * data?.addDiscount.totals.discounts, 2),
      },
    })
    setHideProcessingScreen((prevState) => prevState + 1)
    setDiscountCode("")
    return null
  }

  const payWithCreditCard = async (opaqueData: {
    dataDescriptor: string
    dataValue: string
  }) => {
    setPaymentErrors([])
    if (!opaqueData.dataDescriptor || !opaqueData.dataValue) {
      setPaymentErrors([
        "Please check your billing information below. All fields are required.",
      ])
      setHideProcessingScreen((prevState) => prevState + 1)
      return
    }
    try {
      const { data, errors } = await apolloClient.mutate<
        PayWithCardMutation,
        PayWithCardMutationVariables
      >({
        mutation: PAY_WITH_CARD,
        variables: {
          input: {
            dataDescriptor: opaqueData.dataDescriptor,
            dataValue: opaqueData.dataValue,
          },
        },
      })
      if (errors) {
        setPaymentErrors(
          errors.map((err) => {
            const code = (err.extensions as Record<string, any>)
              ?.gatewayErrorCode
            return code
              ? `${err.message}<br/><span class="font-normal opacity-70">Error code: ${code}</span>`
              : err.message
          })
        )
        setHideProcessingScreen((prevState) => prevState + 1)
        return
      }
      if (!data?.payWithCard?.orderId) {
        setPaymentErrors([
          "There was an error processing your payment. It appears to be on our end. Please try again later",
        ])
        setHideProcessingScreen((prevState) => prevState + 1)
        return
      }
      if (user.isAnonymous || !user.email) {
        window.localStorage.setItem(
          process.env.NEXT_PUBLIC_WINDOW_STORAGE_KEY_EMAIL!,
          emailAddress.trim().toLowerCase()
        )
      }
      window.location.href = `/checkout/complete/${data.payWithCard.orderId}`
    } catch (error: any) {
      setHideProcessingScreen((prevState) => prevState + 1)
      setPaymentErrors([JSON.stringify(error)])
    }
  }

  const onRemoveDiscountCode = async () => {
    setProcessingText("Removing the discount code and updating your cart")
    setIsProcessing(true)
    setCartErrors([])
    setPaymentErrors([])
    useGtm({
      event: "orTrack",
      eventName: "Coupon Removed",
      properties: {
        coupon_id: cart?.discountCode,
        coupon_name: cart?.discountName,
        discount: _round(0.01 * cart?.totals.discounts, 2),
      },
    })
    const { data, errors } = await removeDiscountCode()
    if (errors) {
      setCartErrors(_map(errors, (error) => error.message))
      setHideProcessingScreen((prevState) => prevState + 1)
      return
    }
    setCart(data?.removeDiscount)
    setHideProcessingScreen((prevState) => prevState + 1)
  }

  const onSignOut = () => {
    setProcessingText("Logging you out...")
    setEmailAddress("")
    setIsProcessing(true)
    setCheckoutStep("shipping")
    setPreviousUid(user?.uid ?? null)
  }

  const onSignOutAfterSettingPreviousUid = async () => {
    await dispatch(logOut()).then(() =>
      setHideProcessingScreen((prevState) => prevState + 1)
    )
  }

  const onSubmitShipping = async (
    values: CheckoutShippingRequest
  ): Promise<any> => {
    setCartErrors([])
    setProcessingText("Validating your address")
    setIsProcessing(true)
    const { data, errors } = await setCartShipping({
      ...(user.isAnonymous && { emailAddress }),
      ...(phoneNumber.length > 0 && { phoneNumber }),
      ...values,
    })
    if (errors) {
      setIsProcessing(false)
      setCartErrors(_map(errors, (error) => error.message))
      return
    }
    setCart(data?.cartSetShipping.cart)
    setInitialShippingState(
      data?.cartSetShipping.cart?.shippingAddress?.state ?? values.state
    )
    setEmailAddressSuggestion(data?.cartSetShipping.emailSuggestion ?? null)
    setBillingEnabled(true)
    setIsProcessing(false)
    useGtm({
      event: "orTrack",
      eventName: "Checkout Step Completed",
      properties: {
        step: "Shipping Added",
      },
    })
  }

  const isThcRestricted = useMemo(() => {
    if (initialShippingState === null || hasThc === null) return null
    return Boolean(
      initialShippingState &&
        hasThc &&
        thcRestrictedStates.includes(initialShippingState)
    )
  }, [initialShippingState, hasThc])

  const isThcaRestricted = useMemo(() => {
    if (initialShippingState === null || hasThca === null) return null
    return Boolean(
      initialShippingState &&
        hasThca &&
        thcaRestrictedStates.includes(initialShippingState)
    )
  }, [initialShippingState, hasThca])

  const isDeltaEightRestricted = useMemo(() => {
    if (initialShippingState === null || hasDeltaEight === null) return null
    return Boolean(
      initialShippingState &&
        hasDeltaEight &&
        deltaEightRestrictedStates.includes(initialShippingState)
    )
  }, [initialShippingState, hasDeltaEight])

  const stateIsRestricted = useMemo(() => {
    if (
      isThcRestricted === null ||
      isThcaRestricted === null ||
      isDeltaEightRestricted === null
    )
      return null
    return isThcRestricted || isThcaRestricted || isDeltaEightRestricted
  }, [isThcRestricted, isThcaRestricted, isDeltaEightRestricted])

  const shippingState = useMemo(() => {
    if (initialShippingState === null || stateIsRestricted === null) return null
    return stateIsRestricted ? "" : initialShippingState
  }, [initialShippingState, stateIsRestricted])

  useEffect(() => {
    if (shippingState === null) {
      return
    }
    setHideProcessingScreen((prevState) => prevState + 1)
  }, [shippingState])

  useEffect(() => {
    if (hideProcessingScreen > 0) {
      setIsProcessing(false)
    }
  }, [hideProcessingScreen])

  useEffect(() => {
    if (previousUid) {
      onSignOutAfterSettingPreviousUid()
    }
  }, [previousUid])

  useEffect(() => {
    if (!user.authStateKnown) {
      return
    }
    if (user.authStateKnown && user.uid) {
      if (user.isAnonymous) {
        useGtm({
          event: "orTrack",
          eventName: "Checkout Step Viewed",
          properties: {
            step: "Create Account",
          },
        })
      }
      if (user.isAnonymous && previousUid) {
        apolloClient
          .mutate<
            TransferCartToNewUidMutation,
            TransferCartToNewUidMutationVariables
          >({
            mutation: CART_TRANSFER,
            variables: {
              input: {
                cartId: previousUid!,
              },
            },
          })
          .then(() => getCartAndUser)
      } else {
        getCartAndUser()
      }
    }
  }, [user])

  return (
    <SplitScreenLayout
      browserTitle="Secure Checkout"
      isLoading={isProcessing || !user.authStateKnown}
      loadingText={processingText}
    >
      <div className="font-browser md:col-span-3">
        <div className="p-4 lg:pr-8 lg:max-w-3xl lg:ml-auto xl:pr-16">
          <div className="mb-8">
            <CheckoutBreadcrumbs step={checkoutStep} />
          </div>
          {!isProcessing && user.authStateKnown && (
            <>
              <section className="mb-8 md:hidden">
                <button
                  className="flex bg-orange text-white p-2 w-full items-center justify-between text-sm"
                  onClick={() => {
                    setShowMobileCartSummary((prevState) => !prevState)
                  }}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: `${
                        showMobileCartSummary ? "&#9650;" : " &#9660;"
                      } Order Summary`,
                    }}
                  />
                  <span>{formatCurrency(cart?.totals.total)}</span>
                </button>
                <div
                  data-showing-mobile-checkout-summary={showMobileCartSummary}
                >
                  <div className="my-4">
                    <CheckoutCartSummary
                      cart={cart}
                      discountCode={discountCode}
                      discountCodeError={discountCodeError}
                      onEnterDiscountCode={(ev) => {
                        setDiscountCode(ev.target.value)
                      }}
                      onRemoveDiscountCode={async () => {
                        setDiscountCodeError(null)
                        await onRemoveDiscountCode()
                      }}
                      onSubmitDiscountCode={async () => {
                        setDiscountCodeError(null)
                        const error = await onSubmitDiscountCode()
                        if (error) {
                          setDiscountCodeError(error)
                        }
                      }}
                      salesTaxDescription={getSalesTaxDescriptionLine()}
                    />
                  </div>
                </div>
              </section>
              <section className="mb-8">
                <CheckoutPrePurchase
                  emailAddress={emailAddress}
                  emailAddressSuggestion={emailAddressSuggestion}
                  isProcessing={isProcessing}
                  setEmailAddress={(email) => {
                    setBillingEnabled(false)
                    setEmailAddress(email)
                    setEmailAddressSuggestion(null)
                  }}
                  onLogOut={onSignOut}
                  userEmail={user.email ?? null}
                  userFirstName={user.firstName ?? null}
                  userIsAnonymous={user.isAnonymous}
                />
              </section>
              <section className="mb-8">
                <CheckoutPhoneForm
                  initialPhoneNumber={
                    cart?.shippingAddress?.phoneNumber ?? user.phoneNumber ?? ""
                  }
                  onFormChanged={(phoneProvided) => {
                    setPhoneNumber(phoneProvided)
                  }}
                />
              </section>
              {cart?.allowDiscountCodes && (
                <section className="mb-8 md:hidden">
                  <legend className="block mb-2">
                    <h2 className="font-browser font-medium">
                      Have a Discount Code?
                    </h2>
                    <CheckoutDiscountCode
                      discountCode={discountCode}
                      discountCodeError={discountCodeError}
                      onEnterDiscountCode={(ev) => {
                        setDiscountCode(ev.target.value)
                      }}
                      onSubmitDiscountCode={async () => {
                        setDiscountCodeError(null)
                        const error = await onSubmitDiscountCode()
                        if (error) {
                          setDiscountCodeError(error)
                        }
                      }}
                    />
                  </legend>
                </section>
              )}
              <section className="mb-8">
                <CheckoutShippingForm
                  hasDeltaEight={hasDeltaEight ?? false}
                  hasThc={hasThc ?? false}
                  hasThca={hasThca ?? false}
                  initialFirstName={
                    cart?.shippingAddress?.firstName ?? user.firstName ?? ""
                  }
                  initialLastName={
                    cart?.shippingAddress?.lastName ?? user.lastName ?? ""
                  }
                  initialStreetOne={
                    cart?.shippingAddress?.streetOne ?? user.streetOne ?? ""
                  }
                  initialStreetTwo={
                    cart?.shippingAddress?.streetTwo ?? user.streetTwo ?? ""
                  }
                  initialCity={cart?.shippingAddress?.city ?? user.city ?? ""}
                  initialState={shippingState ?? ""}
                  initialZip={cart?.shippingAddress?.zip ?? user.zip ?? ""}
                  initialCountry={
                    cart?.shippingAddress?.country ?? user.country ?? "US"
                  }
                  isProcessing={isProcessing}
                  onFormChanged={() => {
                    setBillingEnabled(false)
                  }}
                  onSubmit={onSubmitShipping}
                  submitEnabled={!billingEnabled}
                />
              </section>
              {cartErrors.length > 0 && (
                <div className="bg-red/20 border border-red rounded-sm mb-8 p-4 pb-3 font-medium text-sm text-red">
                  {_map(cartErrors, (error, idx) => (
                    <p
                      className="mb-1"
                      key={`shipping_error_${idx}`}
                      dangerouslySetInnerHTML={{ __html: error }}
                    />
                  ))}
                </div>
              )}
              <section
                className={classNames(
                  "mb-8",
                  billingEnabled
                    ? "opacity-100"
                    : "opacity-10 pointer-events-none"
                )}
              >
                <div className="flex justify-between items-center  mb-2">
                  <h2 className="font-browser font-medium text-lg">
                    Billing Information{" "}
                  </h2>
                  {/* <CheckoutApplePayButton
                      enabled={billingEnabled && !isProcessing}
                    /> */}
                </div>
                <p className="text-sm text-gray-900 mb-2 lg:max-w-sm">
                  Enter your card information below to check out with a credit
                  or debit card. Payments are secure and encrypted.
                </p>
                <p className="mb-4 text-xs text-gray-400">
                  By checking out, you expressly agree to our Privacy Policy and
                  Terms of Use. You can opt out of any marketing email by
                  clicking &ldquo;Unsubscribe&rdquo; and out of any SMS messages
                  by replying &ldquo;Stop&rdquo;. If you opt out of marketing
                  messages, we may still send you messages about your orders or
                  other important account information.
                </p>
                {paymentErrors.length > 0 && (
                  <div className="bg-red/20 border border-red rounded-sm mb-8 p-4 pb-3 font-medium text-sm text-red">
                    {_map(paymentErrors, (error, idx) => (
                      <p
                        className="mb-1"
                        key={`payment_error_${idx}`}
                        dangerouslySetInnerHTML={{ __html: error }}
                      />
                    ))}
                  </div>
                )}
                <AuthorizeNetPaymentForm
                  enabled={billingEnabled && !isProcessing}
                  getPaymentToken={async (opaqueData) => {
                    setProcessingText("Processing your order")
                    setIsProcessing(true)
                    await payWithCreditCard(opaqueData)
                  }}
                />
              </section>
            </>
          )}
        </div>
        {user.email && <YotpoLoyaltyLoader email={user.email} uid={user.uid} />}
      </div>
      <div className="font-browser bg-gray-200 border-l border-gray-300 hidden p-4 md:block md:col-span-2 lg:px-8">
        <CheckoutCartSummary
          cart={cart}
          discountCode={discountCode}
          discountCodeError={discountCodeError}
          onEnterDiscountCode={(ev) => {
            setDiscountCode(ev.target.value)
          }}
          onRemoveDiscountCode={async () => {
            setDiscountCodeError(null)
            await onRemoveDiscountCode()
          }}
          onSubmitDiscountCode={async () => {
            setDiscountCodeError(null)
            const error = await onSubmitDiscountCode()
            if (error) {
              setDiscountCodeError(error)
            }
          }}
          salesTaxDescription={getSalesTaxDescriptionLine()}
        />
      </div>
      <LoadingFullScreen showing={isProcessing} text={processingText} />
    </SplitScreenLayout>
  )
}
